import { z } from 'zod';
import { PageMetaSchema, PageQuerySchema, SortDirectionSchema } from './pagination.js';
/**
 * Support cases: severity-driven SLA clocks (organisation commitments
 * override defaults), append-only case events (status changes, comments,
 * communications, attachments), and hardened attachment intake. Clinical
 * content is explicitly prohibited everywhere in this module.
 */
export const CASE_SEVERITIES = ['s1', 's2', 's3', 's4'];
export const CaseSeveritySchema = z.enum(CASE_SEVERITIES);
/** Default SLA minutes per severity; commitments may override per org. */
export const DEFAULT_SLA = {
    s1: { acknowledgeMinutes: 30, resolveMinutes: 240 },
    s2: { acknowledgeMinutes: 60, resolveMinutes: 480 },
    s3: { acknowledgeMinutes: 240, resolveMinutes: 2880 },
    s4: { acknowledgeMinutes: 480, resolveMinutes: 10080 },
};
export const CASE_STATUSES = [
    'new',
    'acknowledged',
    'in_progress',
    'waiting_customer',
    'resolved',
    'closed',
];
export const CaseStatusSchema = z.enum(CASE_STATUSES);
export const CASE_TRANSITIONS = {
    new: ['acknowledged'],
    acknowledged: ['in_progress', 'waiting_customer', 'resolved'],
    in_progress: ['waiting_customer', 'resolved'],
    waiting_customer: ['in_progress', 'resolved'],
    resolved: ['closed', 'in_progress'],
    closed: [],
};
export const CaseCreateSchema = z
    .object({
    organisationId: z.uuid(),
    title: z.string().trim().min(5).max(200),
    description: z.string().trim().min(5).max(4000),
    severity: CaseSeveritySchema,
    assignedToUserId: z.uuid().optional(),
})
    .strict();
export const CaseTransitionSchema = z
    .object({
    toStatus: CaseStatusSchema,
    note: z.string().trim().min(3).max(2000),
    expectedVersion: z.number().int().min(1),
})
    .strict();
export const CaseAssignSchema = z
    .object({
    assignedToUserId: z.uuid().nullable(),
    expectedVersion: z.number().int().min(1),
})
    .strict();
export const COMMUNICATION_CHANNELS = ['email', 'phone', 'portal'];
export const CaseCommunicationSchema = z
    .object({
    direction: z.enum(['inbound', 'outbound']),
    channel: z.enum(COMMUNICATION_CHANNELS),
    summary: z.string().trim().min(3).max(2000),
})
    .strict();
export const CASE_EVENT_KINDS = [
    'created',
    'status_change',
    'assignment',
    'comment',
    'communication',
    'attachment_added',
    'attachment_rejected',
];
export const CaseEventSchema = z.object({
    id: z.uuid(),
    caseId: z.uuid(),
    kind: z.enum(CASE_EVENT_KINDS),
    body: z.string().nullable(),
    meta: z.record(z.string(), z.unknown()).nullable(),
    actorUserId: z.uuid().nullable(),
    actorDisplayName: z.string().nullable(),
    occurredAt: z.iso.datetime(),
});
export const SupportCaseSchema = z.object({
    id: z.uuid(),
    caseNumber: z.number().int(),
    organisationId: z.uuid(),
    organisationName: z.string(),
    title: z.string(),
    description: z.string(),
    severity: CaseSeveritySchema,
    status: CaseStatusSchema,
    assignedToUserId: z.uuid().nullable(),
    assignedToDisplayName: z.string().nullable(),
    openedByDisplayName: z.string().nullable(),
    openedAt: z.iso.datetime(),
    acknowledgedAt: z.iso.datetime().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    closedAt: z.iso.datetime().nullable(),
    sla: z.object({
        acknowledgeBy: z.iso.datetime(),
        resolveBy: z.iso.datetime(),
        acknowledgeBreached: z.boolean(),
        resolveBreached: z.boolean(),
        source: z.enum(['default', 'commitment']),
    }),
    version: z.number().int(),
});
export const CaseListQuerySchema = PageQuerySchema.extend({
    organisationId: z.uuid().optional(),
    status: CaseStatusSchema.optional(),
    severity: CaseSeveritySchema.optional(),
    assignedToUserId: z.uuid().optional(),
    open: z.stringbool().optional(),
    breached: z.stringbool().optional(),
    sortBy: z.enum(['openedAt', 'severity', 'status', 'acknowledgeBy']).default('openedAt'),
    sortDir: SortDirectionSchema.default('desc'),
});
export const CaseListResponseSchema = z.object({
    items: z.array(SupportCaseSchema),
    meta: PageMetaSchema,
});
/* ------------------------------------------------------------ attachments */
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
/** Allowlisted content types with their magic-byte signatures. */
export const ATTACHMENT_ALLOWLIST = {
    'image/png': { extensions: ['png'], magic: 'png' },
    'image/jpeg': { extensions: ['jpg', 'jpeg'], magic: 'jpeg' },
    'application/pdf': { extensions: ['pdf'], magic: 'pdf' },
    'text/plain': { extensions: ['txt'], magic: 'text' },
    'text/csv': { extensions: ['csv'], magic: 'text' },
};
export const AttachmentSchema = z.object({
    id: z.uuid(),
    caseId: z.uuid(),
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    sha256: z.string(),
    uploadedByDisplayName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
//# sourceMappingURL=cases.js.map